import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FiUser, FiMail, FiPhone, FiMapPin, FiCamera, FiSave, FiEdit } from "react-icons/fi";
import { UpdateUserProfileDashboard } from "../../redux/actions/User";

const UserProfile = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const loading = useSelector((state) => state.user.isLoading);
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    bio: '',
    website: '',
    socialMedia: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: ''
    }
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        name: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.name || '',
        email: user.email || '',
        phone: user.phoneNumber || user.phone || '',
        address: user.address || '',
        bio: user.bio || '',
        website: user.website || '',
        socialMedia: {
          facebook: user.socialMedia?.facebook || '',
          twitter: user.socialMedia?.twitter || '',
          instagram: user.socialMedia?.instagram || '',
          linkedin: user.socialMedia?.linkedin || ''
        }
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setProfileData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setProfileData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSave = async () => {
    try {
      const result = await dispatch(UpdateUserProfileDashboard(profileData));
      if (result.success) {
        setIsEditing(false);
      } else {
        alert('Failed to update profile. Please try again.');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  const handleCancel = () => {
    // Reset to original user data
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        bio: user.bio || '',
        website: user.website || '',
        socialMedia: {
          facebook: user.socialMedia?.facebook || '',
          twitter: user.socialMedia?.twitter || '',
          instagram: user.socialMedia?.instagram || '',
          linkedin: user.socialMedia?.linkedin || ''
        }
      });
    }
    setIsEditing(false);
  };

  return (
    <div className="user-dashboard-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Profile Settings</h2>
          <p className="text-gray-600">Manage your account information and preferences</p>
        </div>
        
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="user-action-button flex items-center"
          >
            <FiEdit className="w-4 h-4 mr-2" />
            Edit Profile
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-color2"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="user-action-button flex items-center"
            >
              <FiSave className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Picture Section */}
        <div className="user-dashboard-card">
          <div className="text-center">
            <div className="relative inline-block">
              <div className="h-32 w-32 rounded-full bg-color2 flex items-center justify-center mx-auto mb-4">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt="Profile"
                    className="h-32 w-32 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-4xl font-medium text-white">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              {isEditing && (
                <button className="absolute bottom-0 right-0 h-10 w-10 rounded-full bg-gray-600 text-white flex items-center justify-center hover:bg-gray-700">
                  <FiCamera className="h-5 w-5" />
                </button>
              )}
            </div>
            <h3 className="text-lg font-medium text-gray-900">{user?.name}</h3>
            <p className="text-sm text-gray-500">{user?.email}</p>
            <p className="text-sm text-gray-400 mt-2">
              Member since {new Date(user?.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Profile Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="user-dashboard-card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="name"
                    value={profileData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-color2 focus:border-color2"
                  />
                ) : (
                  <div className="flex items-center py-2">
                    <FiUser className="h-5 w-5 text-gray-400 mr-2" />
                    <span>{user?.name || 'Not provided'}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-color2 focus:border-color2"
                  />
                ) : (
                  <div className="flex items-center py-2">
                    <FiMail className="h-5 w-5 text-gray-400 mr-2" />
                    <span>{user?.email || 'Not provided'}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    name="phone"
                    value={profileData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-color2 focus:border-color2"
                  />
                ) : (
                  <div className="flex items-center py-2">
                    <FiPhone className="h-5 w-5 text-gray-400 mr-2" />
                    <span>{user?.phone || 'Not provided'}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="address"
                    value={profileData.address}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-color2 focus:border-color2"
                  />
                ) : (
                  <div className="flex items-center py-2">
                    <FiMapPin className="h-5 w-5 text-gray-400 mr-2" />
                    <span>{user?.address || 'Not provided'}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bio and Website */}
          <div className="user-dashboard-card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">About</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bio
                </label>
                {isEditing ? (
                  <textarea
                    name="bio"
                    rows="3"
                    value={profileData.bio}
                    onChange={handleInputChange}
                    placeholder="Tell us about yourself..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-color2 focus:border-color2"
                  />
                ) : (
                  <p className="text-gray-600 py-2">
                    {user?.bio || 'No bio provided yet.'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                {isEditing ? (
                  <input
                    type="url"
                    name="website"
                    value={profileData.website}
                    onChange={handleInputChange}
                    placeholder="https://yourwebsite.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-color2 focus:border-color2"
                  />
                ) : (
                  <p className="text-gray-600 py-2">
                    {user?.website ? (
                      <a 
                        href={user.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-color2 hover:underline"
                      >
                        {user.website}
                      </a>
                    ) : (
                      'No website provided'
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div className="user-dashboard-card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Social Media</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['facebook', 'twitter', 'instagram', 'linkedin'].map((platform) => (
                <div key={platform}>
                  <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                    {platform}
                  </label>
                  {isEditing ? (
                    <input
                      type="url"
                      name={`socialMedia.${platform}`}
                      value={profileData.socialMedia[platform]}
                      onChange={handleInputChange}
                      placeholder={`https://${platform}.com/username`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-color2 focus:border-color2"
                    />
                  ) : (
                    <p className="text-gray-600 py-2">
                      {user?.socialMedia?.[platform] ? (
                        <a 
                          href={user.socialMedia[platform]} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-color2 hover:underline"
                        >
                          {user.socialMedia[platform]}
                        </a>
                      ) : (
                        'Not connected'
                      )}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;